import InputStyles from '@components/InputFieldControlled/InputText.styles';
import { BaseInputProps } from '@components/InputFieldControlled/InputText.types';
import { FlexRow } from '@components/FlexRow';
import { isArray } from 'lodash';
import React from 'react';
import { Controller } from 'react-hook-form';
import composeRules, { getValidationRules } from '@utils/composeRules';
import {
    Box,
    Chip as MuiChip,
    styled,
    useTheme,
    TextField,
    Popper,
    Typography,
    FormLabel,
    FormControl,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { Autocomplete } from '@components/AutoComplete';

type Option = { 
    value: any; 
    label: string; 
    isDisabled?: boolean; 
    icon?: string; 
    isFixed?: boolean; 
    url?: string; 
};

type GroupedOption = { 
    options: Option[]; 
    label: string; 
    isDisabled?: boolean; 
    icon?: string; 
    isFixed?: boolean; 
};

type InputMultiSelectProps = {
    options?: Option[];
    groupedOptions?: GroupedOption[];
    isLanguage?: boolean;
    single?: boolean;
    items?: any[];
    hideValue?: boolean;
    notClearable?: boolean;
} & BaseInputProps;

const StyledPopper = styled(Popper)(({ theme }) => ({
    // zIndex: theme.zIndex.modal,
    // '& .MuiPaper-root': {
    //     borderRadius: 8,
    //     marginTop: 4,
    //     boxShadow: theme.shadows[8],
    //     '& .MuiAutocomplete-option': {
    //         fontSize: 14,
    //         fontWeight: 600,
    //         padding: '12px',
    //         borderRadius: 8,
    //         margin: '0 8px',
    //         '&.Mui-focused': {
    //             backgroundColor: theme.palette.action.selected,
    //         },
    //         '&[aria-disabled="true"]': {
    //             opacity: 0.5,
    //             pointerEvents: 'none',
    //         },
    //     },
    //     '& .MuiAutocomplete-groupLabel': {
    //         padding: '4px 16px',
    //         display: 'inline-block',
    //         margin: '16px 12px 8px 12px',
    //         width: 'auto',
    //         color: theme.palette.text.primary,
    //         fontWeight: 700,
    //         fontStyle: 'italic',
    //         // borderRadius: '16px',
    //         border: `1px solid ${theme.palette.divider}`,
    //         fontSize: 14,
    //     },
    //     '& .MuiAutocomplete-groupUl': {
    //         padding: '0 0 16px 0',
    //         borderTop: `1px solid ${theme.palette.divider}`,
    //         '&:first-of-type': {
    //             borderTop: 'none',
    //         },
    //     },
    // },
}));

const Chip = styled(MuiChip)(({ theme }) => ({
    
}));

const FlagImageContainer = styled(Box)(() => ({
    width: 20,
    height: 14,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 3,
    overflow: 'hidden',
    marginRight: 4,
}));

const FlagImage = styled('img')(({ theme }) => ({
    height: 14,
    width: 20,
    borderWidth: 0.2,
    borderColor: theme.palette.divider,
    borderStyle: 'solid',
}));

const OptionContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const ChipContainer = styled(Box)(() => ({
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
}));

const ChipIcon = styled(Box)(({ theme }) => ({
    position: 'absolute',
    left: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1,
}));

const PlaceholderOverlay = styled(Box)(({ theme }) => ({
    position: 'absolute',
    zIndex: 2,
    padding: '12px 18px',
    pointerEvents: 'none',
}));

const InputMultiSelect = (props: InputMultiSelectProps) => {
    const theme = useTheme();
    
    // Process options
    const groupedOptions = props.groupedOptions ?? props.items?.map(item => ({
        label: item.name,
        options: item.children?.map(child => ({
            ...child,
            label: child.name,
            value: child.id,
        })) ?? [],
    })) ?? [];
    
    const rules = composeRules(props.required, props.rules);
    const isGrouped = groupedOptions.length > 0;
    const flatGroupOptions = groupedOptions?.reduce((acc, group) => [...acc, ...(group.options ?? [])], []);
    const flattenOptions = isGrouped ? flatGroupOptions : props.options ?? [];
    const allOptionsWithGroups = isGrouped ? flatGroupOptions : props.options ?? [];

    // Create grouped options for Autocomplete - options are processed inline

    const ImageFlag = ({ url }: { url?: string }) => (
        <FlagImageContainer>
            <FlagImage
                src={url ?? 'https://img.freepik.com/free-vector/white-abstract-background_23-2148806276.jpg'}
                alt="flag"
            />
        </FlagImageContainer>
    );

    const renderOption = (renderProps: React.HTMLAttributes<HTMLLIElement>, option: Option) => (
        <Box component="li" {...renderProps} key={option.value}>
            <OptionContainer>
                {props.isLanguage && <ImageFlag url={option.url} />}
                {option.icon && (
                    <i className={`fi fi-br-${option.icon}`} style={{ fontSize: 20, color: theme.palette.text.secondary }} />
                )}
                {option.label}
            </OptionContainer>
        </Box>
    );

    const renderTags = (value: Option[], getTagProps: any) =>
        value.map((option, index) => (
            <ChipContainer key={option.value}>
                {option?.isFixed && (
                    <ChipIcon>
                        <i
                            className="fi-sr-star"
                            style={{ 
                                color: theme.palette.primary.main, 
                                fontSize: 12 
                            }}
                        />
                    </ChipIcon>
                )}
                <MuiChip
                    {...getTagProps({ index })}
                    label={
                        <OptionContainer>
                            {props.isLanguage && <ImageFlag url={option.url} />}
                            {option.icon && (
                                <i className={`fi fi-br-${option.icon}`} style={{ fontSize: 16, color: option.isFixed ? theme.palette.primary.main : theme.palette.text.secondary }} />
                            )}
                            {option.label}
                        </OptionContainer>
                    }
                    deleteIcon={option.isFixed ? undefined : <ClearIcon />}
                    onDelete={option.isFixed ? undefined : getTagProps({ index }).onDelete}
                    
                />
            </ChipContainer>
        ));

    return (
        <Controller
            control={props.control}
            name={props.name}
            defaultValue={props.defaultValue ?? (props.single ? null : [])}
            rules={{
                validate: getValidationRules(rules ?? {}),
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
                // Convert values to option objects
                const selectedOptions = props.single 
                    ? flattenOptions.find(option => option.value === value) || null
                    : (value || []).map((val: any) => flattenOptions.find(option => option.value === val)).filter(Boolean);

                return (
                    <FormControl
                        sx={[
                            InputStyles.container,
                            props.viewOnly ? InputStyles.viewOnly : {},
                            props.fullWidth ? { maxWidth: 'unset' } : {},
                            props.containerStyle,
                        ]}
                    >
                        {props.label && (
                            <FormLabel>
                                {props.label}
                            </FormLabel >
                        )}

                        {props.viewOnly ? (
                            <FlexRow gap={2}>
                                {isArray(value) ? 
                                    value.map((val: any, index: number) => {
                                        const item = flattenOptions.find(option => option.value === val);
                                        return item ? (
                                            <ChipContainer key={index}>
                                                {item.isFixed && (
                                                    <ChipIcon>
                                                        <i
                                                            className="fi-sr-star"
                                                            style={{ 
                                                                color: theme.palette.primary.main, 
                                                                fontSize: 12 
                                                            }}
                                                        />
                                                    </ChipIcon>
                                                )}
                                                <Chip
                                                    sx={{
                                                        backgroundColor: item.isFixed
                                                            ? theme.palette.action.selected
                                                            : theme.palette.grey[100],
                                                        color: item.isFixed
                                                            ? theme.palette.primary.main
                                                            : theme.palette.text.primary,
                                                    }}
                                                    label={
                                                        <OptionContainer>
                                                            {props.isLanguage && <ImageFlag url={item.url} />}
                                                            {item.icon && (
                                                                <i className={`fi fi-br-${item.icon}`} style={{ fontSize: 16, color: item.isFixed ? theme.palette.primary.main : theme.palette.text.secondary }} />
                                                            )}
                                                            {item.label}
                                                        </OptionContainer>
                                                    }
                                                />
                                            </ChipContainer>
                                        ) : null;
                                    })
                                    :
                                    (() => {
                                        const item = flattenOptions.find(option => option.value === value);
                                        return item ? <Chip label={item.label} /> : null;
                                    })()
                                }
                            </FlexRow>
                        ) : (
                            <Box sx={{ position: 'relative' }}>
                                {props.hideValue && (value ?? []).length !== 0 && (
                                    <PlaceholderOverlay>
                                        <Typography
                                            sx={{
                                                color: theme.palette.text.secondary,
                                                fontWeight: 400,
                                                fontSize: 14,
                                            }}
                                        >
                                            {props.placeholder ?? props.label}
                                        </Typography>
                                    </PlaceholderOverlay>
                                )}
                                
                                <Autocomplete
                                    multiple={!props.single}
                                    options={allOptionsWithGroups}
                                    value={selectedOptions}
                                    onChange={(event, newValue) => {
                                        if (props.single) {
                                            const selectedValue = (newValue as Option)?.value ?? null;
                                            onChange(selectedValue);
                                            props.onChange?.(selectedValue, newValue);
                                        } else {
                                            const currentValues = (value ?? []);
                                            const newValues = newValue as Option[];
                                            
                                            // Check if trying to remove fixed values
                                            const removedItems = currentValues.filter((val: any) => 
                                                !newValues.some(opt => opt.value === val)
                                            );
                                            
                                            const hasRemovedFixedItem = removedItems.some((val: any) => {
                                                const option = flattenOptions.find(opt => opt.value === val);
                                                return option?.isFixed;
                                            });
                                            
                                            if (hasRemovedFixedItem) {
                                                return; // Prevent removing fixed items
                                            }
                                            
                                            const selectedValues = newValues.map(option => option.value);
                                            onChange(selectedValues);
                                            props.onChange?.(selectedValues, newValues);
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder={!props.hideValue || (value ?? []).length === 0 ? (props.placeholder ?? props.label) : ''}
                                            error={!!error}
                                        />
                                    )}
                                    renderOption={renderOption}
                                    renderTags={props.single ? undefined : renderTags}
                                    getOptionLabel={(option) => (option as Option)?.label || ''}
                                    isOptionEqualToValue={(option, value) => (option as Option).value === (value as Option).value}
                                    getOptionDisabled={(option) => !!(option as Option).isDisabled}
                                    groupBy={isGrouped ? (option) => (option as any).group : undefined}
                                    disableClearable={props.notClearable}
                                    disabled={props.disabled}
                                    PopperComponent={StyledPopper}
                                    // error={!!error}
                                    // hideValue={props.hideValue}
                                />
                            </Box>
                        )}
                        {!props.viewOnly && (
                            <>
                                {error?.message ? (
                                    <Typography color="error" variant="caption">{error.message}</Typography>
                                ) : props.helpText ? (
                                    <span style={InputStyles.subText}>{props.helpText}</span>
                                ) : null}
                            </>
                        )}
                    </FormControl>
                );
            }}
        />
    );
};

export default InputMultiSelect;
